import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ErrorNote, Field, Pager, Panel, StatusPill } from '../components/ui';
import { date, likeEscape, money } from '../lib/format';
import { supabase } from '../lib/supabase';

interface CustomerRow {
  id: string;
  name: string;
  type: string;
  email: string | null;
  phone: string | null;
  min_markup: number | null;
  created_at: string;
}

const PAGE = 50;

export default function Customers() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      let query = supabase
        .from('customers')
        .select('id, name, type, email, phone, min_markup, created_at', { count: 'exact' })
        .eq('archived', false)
        .order('name')
        .range(page * PAGE, page * PAGE + PAGE - 1);
      const s = q.trim();
      if (s) {
        const p = `%${likeEscape(s)}%`;
        query = query.or(`name.ilike.${p},email.ilike.${p},phone.ilike.${p}`);
      }
      const { data, count } = await query;
      setRows((data as CustomerRow[]) ?? []);
      setTotal(count ?? 0);
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, page, reload]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Customers</h1>
        <input className="search" placeholder="Search name, email or phone" value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        <span className="spacer" />
        <button className="primary" onClick={() => nav('/customers/new')}>+ Add customer</button>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Phone</th><th className="num">Min markup</th><th>Added</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`click ${r.id === id ? 'sel' : ''}`} onClick={() => nav(`/customers/${r.id}`)}>
                  <td><b>{r.name}</b>{r.type === 'person' && <span className="tag" style={{ marginLeft: 6 }}>person</span>}</td>
                  <td>{r.email}</td>
                  <td>{r.phone}</td>
                  <td className="num">{r.min_markup != null ? `${r.min_markup}%` : ''}</td>
                  <td className="muted">{date(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && <div className="empty">No customers match “{q}”.</div>}
          {loading && rows.length === 0 && <div className="empty">Loading…</div>}
        </div>
        <Pager page={page} pageSize={PAGE} total={total} onPage={setPage} />
      </div>
      {id && (
        <CustomerPanel key={id} id={id === 'new' ? null : id} onClose={() => nav('/customers')}
          onSaved={(newId) => { setReload((n) => n + 1); if (newId) nav(`/customers/${newId}`, { replace: true }); }} />
      )}
    </div>
  );
}

interface Customer {
  id?: string;
  type: string;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  industry: string | null;
  min_markup: number | null;
  sales_discount: number | null;
  notes: string | null;
}
interface Contact { id: string; first_name: string | null; last_name: string | null; email: string | null; phone: string | null; is_primary: boolean }
interface Address { id: string; label: string | null; line1: string | null; line2: string | null; city: string | null; region: string | null; postcode: string | null; country: string | null; is_billing: boolean; is_shipping: boolean }

const blank: Customer = { type: 'company', name: '', email: null, phone: null, website: null, industry: null, min_markup: null, sales_discount: null, notes: null };

export function CustomerPanel({ id, onClose, onSaved }: { id: string | null; onClose: () => void; onSaved: (newId?: string) => void }) {
  const [c, setC] = useState<Customer>(blank);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [tab, setTab] = useState<'details' | 'quotes' | 'jobs'>('details');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const [cu, ct, ad, qu, jo] = await Promise.all([
      supabase.from('customers').select('*').eq('id', id).single(),
      supabase.from('contacts').select('*').eq('customer_id', id).eq('archived', false).order('is_primary', { ascending: false }),
      supabase.from('addresses').select('*').eq('customer_id', id).eq('archived', false),
      supabase.from('quote_list').select('*').eq('customer_id', id).order('number', { ascending: false }),
      supabase.from('job_list').select('*').eq('customer_id', id).order('number', { ascending: false }),
    ]);
    if (cu.error) return setError(cu.error.message);
    setC(cu.data as Customer);
    setContacts((ct.data as Contact[]) ?? []);
    setAddresses((ad.data as Address[]) ?? []);
    setQuotes(qu.data ?? []);
    setJobs(jo.data ?? []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const set = (k: keyof Customer, v: any) => { setC({ ...c, [k]: v === '' ? null : v }); setSaved(false); };

  const save = async () => {
    if (!c.name?.trim()) return setError('Name is required.');
    setBusy(true);
    setError(null);
    const body = { type: c.type, name: c.name.trim(), email: c.email, phone: c.phone, website: c.website,
      industry: c.industry, min_markup: c.min_markup, sales_discount: c.sales_discount, notes: c.notes };
    const res = id
      ? await supabase.from('customers').update(body).eq('id', id).select('id').single()
      : await supabase.from('customers').insert(body).select('id').single();
    setBusy(false);
    if (res.error) return setError(res.error.message);
    setSaved(true);
    onSaved(id ? undefined : (res.data as { id: string }).id);
  };

  return (
    <Panel title={id ? c.name || '…' : 'New customer'} sub={id ? `${contacts.length} contacts · ${quotes.length} quotes · ${jobs.length} jobs` : undefined}
      onClose={onClose}
      actions={id && <Link className="btn" to={`/quotes/new?customer=${id}`}>+ New quote</Link>}>
      {id && (
        <div className="tabs">
          {(['details', 'quotes', 'jobs'] as const).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)} {t !== 'details' && <span className="count">{t === 'quotes' ? quotes.length : jobs.length}</span>}
            </button>
          ))}
        </div>
      )}

      {tab === 'details' && (
        <div className="stack">
          <div className="card card-pad stack">
            <div className="grid2">
              <Field label="Name"><input value={c.name ?? ''} onChange={(e) => set('name', e.target.value)} autoFocus={!id} /></Field>
              <Field label="Type">
                <select value={c.type} onChange={(e) => set('type', e.target.value)}>
                  <option value="company">Company</option><option value="person">Person</option>
                </select>
              </Field>
              <Field label="Email"><input type="email" value={c.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label="Phone"><input value={c.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
              <Field label="Website"><input value={c.website ?? ''} onChange={(e) => set('website', e.target.value)} /></Field>
              <Field label="Industry"><input value={c.industry ?? ''} onChange={(e) => set('industry', e.target.value)} /></Field>
              <Field label="Minimum markup %"><input className="num" inputMode="decimal" value={c.min_markup ?? ''} onChange={(e) => set('min_markup', e.target.value)} /></Field>
              <Field label="Sales discount %"><input className="num" inputMode="decimal" value={c.sales_discount ?? ''} onChange={(e) => set('sales_discount', e.target.value)} /></Field>
            </div>
            <Field label="Notes"><textarea value={c.notes ?? ''} onChange={(e) => set('notes', e.target.value)} /></Field>
            <ErrorNote error={error} />
            <div className="row">
              <button className="primary" onClick={save} disabled={busy}>{id ? 'Save changes' : 'Create customer'}</button>
              {saved && <span className="muted">Saved</span>}
            </div>
          </div>

          {id && <Contacts customerId={id} contacts={contacts} onChange={load} />}
          {id && <Addresses customerId={id} addresses={addresses} onChange={load} />}
        </div>
      )}

      {tab === 'quotes' && (
        <div className="card">
          <table className="grid">
            <thead><tr><th>#</th><th>Reference</th><th>Created</th><th>Status</th><th className="num">Total</th></tr></thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id}>
                  <td><Link to={`/quotes/${q.id}`}>{q.number}</Link></td>
                  <td>{q.reference}</td>
                  <td className="muted">{date(q.created_at)}</td>
                  <td><StatusPill label={q.status} colour={q.status_colour} /></td>
                  <td className="num">{money(q.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {quotes.length === 0 && <div className="empty">No quotes yet.</div>}
        </div>
      )}

      {tab === 'jobs' && (
        <div className="card">
          <table className="grid">
            <thead><tr><th>#</th><th>Title</th><th>Due</th><th>Status</th></tr></thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td><Link to={`/jobs/${j.id}`}>{j.number}</Link></td>
                  <td>{j.title}</td>
                  <td>{date(j.deadline)}</td>
                  <td><StatusPill label={j.status} colour={j.status_colour} /> {j.is_offshore && <span className="tag off">Offshore</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {jobs.length === 0 && <div className="empty">No jobs yet.</div>}
        </div>
      )}
    </Panel>
  );
}

function Contacts({ customerId, contacts, onChange }: { customerId: string; contacts: Contact[]; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    const { error } = await supabase.from('contacts').insert({ customer_id: customerId, ...f, is_primary: contacts.length === 0 });
    if (error) return setError(error.message);
    setF({ first_name: '', last_name: '', email: '', phone: '' });
    setAdding(false);
    onChange();
  };
  const remove = async (id: string) => {
    if (!window.confirm('Remove this contact?')) return;
    await supabase.from('contacts').update({ archived: true }).eq('id', id);
    onChange();
  };

  return (
    <div className="card card-pad">
      <div className="row"><h3 className="grow" style={{ margin: 0 }}>Contacts</h3>
        <button className="small" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add contact'}</button></div>
      <table className="grid" style={{ marginTop: 8 }}>
        <tbody>
          {contacts.map((ct) => (
            <tr key={ct.id}>
              <td><b>{[ct.first_name, ct.last_name].filter(Boolean).join(' ') || '—'}</b>{ct.is_primary && <span className="tag" style={{ marginLeft: 6 }}>primary</span>}</td>
              <td>{ct.email && <a href={`mailto:${ct.email}`}>{ct.email}</a>}</td>
              <td>{ct.phone}</td>
              <td className="num"><button className="ghost small danger" onClick={() => remove(ct.id)}>Remove</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {contacts.length === 0 && !adding && <div className="muted small" style={{ padding: 8 }}>No contacts.</div>}
      {adding && (
        <div className="stack" style={{ marginTop: 10 }}>
          <div className="grid4">
            <input placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
            <input placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
            <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </div>
          <ErrorNote error={error} />
          <div><button className="primary small" onClick={add}>Add contact</button></div>
        </div>
      )}
    </div>
  );
}

function Addresses({ customerId, addresses, onChange }: { customerId: string; addresses: Address[]; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const empty = { label: 'Shipping', line1: '', line2: '', city: '', region: '', postcode: '', country: 'New Zealand', is_billing: false, is_shipping: true };
  const [f, setF] = useState(empty);

  const add = async () => {
    await supabase.from('addresses').insert({ customer_id: customerId, ...f });
    setF(empty);
    setAdding(false);
    onChange();
  };

  return (
    <div className="card card-pad">
      <div className="row"><h3 className="grow" style={{ margin: 0 }}>Addresses</h3>
        <button className="small" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add address'}</button></div>
      <div className="grid2" style={{ marginTop: 8 }}>
        {addresses.map((a) => (
          <div key={a.id} className="small" style={{ padding: 8, border: '1px solid var(--line-2)', borderRadius: 6 }}>
            <div className="row" style={{ marginBottom: 4 }}>
              <b className="grow">{a.label}</b>
              {a.is_billing && <span className="tag">billing</span>}{a.is_shipping && <span className="tag">shipping</span>}
            </div>
            {[a.line1, a.line2, [a.city, a.postcode].filter(Boolean).join(' '), a.region, a.country].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
          </div>
        ))}
      </div>
      {addresses.length === 0 && !adding && <div className="muted small" style={{ padding: 8 }}>No addresses.</div>}
      {adding && (
        <div className="stack" style={{ marginTop: 10 }}>
          <div className="grid3">
            <input placeholder="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
            <input placeholder="Address line 1" value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} />
            <input placeholder="Address line 2" value={f.line2} onChange={(e) => setF({ ...f, line2: e.target.value })} />
            <input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
            <input placeholder="Region" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} />
            <input placeholder="Postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} />
          </div>
          <div className="row">
            <label className="check"><input type="checkbox" checked={f.is_billing} onChange={(e) => setF({ ...f, is_billing: e.target.checked })} /> Billing</label>
            <label className="check"><input type="checkbox" checked={f.is_shipping} onChange={(e) => setF({ ...f, is_shipping: e.target.checked })} /> Shipping</label>
            <span className="grow" />
            <button className="primary small" onClick={add}>Add address</button>
          </div>
        </div>
      )}
    </div>
  );
}
